import { computed, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { ConnectionStats } from '@4players/odin';
import { OdinService } from './odin.service';
import { StreamStatsService } from './stream-stats.service';
import { StreamHealthService } from './stream-health.service';
import { MovingAverage } from '../../shared/helpers/moving-average';
import { isTauri } from '../../shared/helpers/platform';

/** Samples kept per series: one minute at the SDK's 1 s stats interval. */
export const STATS_HISTORY = 60;

/** Ping at or above this is shown as fair, at or above the next as poor. */
export const RTT_FAIR_MS = 80;
export const RTT_POOR_MS = 120;
/** Loss above this is shown as fair, above the next as poor. */
export const LOSS_FAIR_PCT = 1;
export const LOSS_POOR_PCT = 5;

/**
 * Samples the displayed ping averages over. The native transport reports
 * QUIC's smoothed RTT (RFC 9002), which needs no second filter; the web SDK
 * reports one ping per interval, which a short average steadies.
 */
const RTT_AVERAGE_WINDOW = isTauri ? 1 : 5;

export type ConnectionQuality = 'good' | 'fair' | 'poor';

export interface ConnectionHistory {
  rtt: number[];
  loss: number[];
  /** Voice and video traffic together. */
  downKbps: number[];
  upKbps: number[];
}

/**
 * Room-level connection stats of the current ODIN room with a one-minute
 * history, for the ping indicator and the connection panel. Owns the room's
 * `onConnectionStats` callback. ODIN's numbers only cover the voice
 * connection; video runs over a separate WebRTC connection, whose traffic is
 * added to download and upload.
 */
@Injectable({ providedIn: 'root' })
export class ConnectionStatsService {
  private odin = inject(OdinService);
  private streamStats = inject(StreamStatsService);
  private streamHealth = inject(StreamHealthService);
  private rttAverage = new MovingAverage(RTT_AVERAGE_WINDOW);
  private previous: { at: number; stats: ConnectionStats } | null = null;
  // Video traffic lags one tick behind: each tick adds the rate measured by
  // the previous video stats poll and starts the next one.
  private videoKbps = { down: 0, up: 0 };
  private videoPrevious: { at: number; sent: number; received: number } | null = null;
  private videoInFlight = false;
  // Discards video polls that finish after the room changed.
  private generation = 0;

  private readonly _latest = signal<ConnectionStats | null>(null);
  private readonly _history = signal<ConnectionHistory>(emptyHistory());
  private readonly _packetsPerSec = signal<{ down: number; up: number } | null>(null);
  private readonly _connectedSince = signal<number | null>(null);

  readonly latest = this._latest.asReadonly();
  readonly history = this._history.asReadonly();
  readonly packetsPerSec = this._packetsPerSec.asReadonly();
  readonly connectedSince = this._connectedSince.asReadonly();
  /** Ping averaged over the last few samples (see RTT_AVERAGE_WINDOW). */
  readonly rtt = signal(0);
  readonly loss = computed(() => this._latest()?.packetLoss ?? 0);
  readonly quality = computed<ConnectionQuality>(() => {
    const rtt = this.rtt();
    const loss = this.loss();
    if (rtt >= RTT_POOR_MS || loss > LOSS_POOR_PCT) return 'poor';
    if (rtt >= RTT_FAIR_MS || loss > LOSS_FAIR_PCT) return 'fair';
    return 'good';
  });

  constructor() {
    this.odin.odinRoom$.pipe(takeUntilDestroyed()).subscribe((room) => {
      this.reset();
      if (!room) return;
      this._connectedSince.set(Date.now());
      room.onConnectionStats = (stats) => this.add(stats);
    });
  }

  /** The current numbers and history, for reports. */
  diagnostics(): unknown {
    return {
      latest: this._latest(),
      rttAverage: this.rtt(),
      packetsPerSec: this._packetsPerSec(),
      videoKbps: this.videoKbps,
      history: this._history(),
    };
  }

  private add(stats: ConnectionStats): void {
    const now = Date.now();
    this._latest.set(stats);
    this.rtt.set(this.rttAverage.addValue(stats.rtt));
    if (this.previous) {
      const dt = (now - this.previous.at) / 1000;
      const before = this.previous.stats;
      // Platforms without packet counters report them as 0: no rate then.
      const counted = stats.packetsReceived > 0 || stats.packetsSent > 0;
      if (counted && dt > 0.2 && stats.packetsReceived >= before.packetsReceived) {
        this._packetsPerSec.set({
          down: Math.round((stats.packetsReceived - before.packetsReceived) / dt),
          up: Math.round((stats.packetsSent - before.packetsSent) / dt),
        });
      }
    }
    this.previous = { at: now, stats };
    this._history.update((h) => ({
      rtt: push(h.rtt, stats.rtt),
      loss: push(h.loss, stats.packetLoss),
      downKbps: push(h.downKbps, (stats.bytesReceivedLastSecond * 8) / 1000 + this.videoKbps.down),
      upKbps: push(h.upKbps, (stats.bytesSentLastSecond * 8) / 1000 + this.videoKbps.up),
    }));
    void this.pollVideo();
  }

  /**
   * Only while tiles show video streams: then the stream health polls the
   * same shared stats snapshot anyway, and without them there is no video
   * traffic to measure.
   */
  private async pollVideo(): Promise<void> {
    if (this.streamHealth.streams().length === 0) {
      this.videoKbps = { down: 0, up: 0 };
      this.videoPrevious = null;
      return;
    }
    if (this.videoInFlight) return;
    this.videoInFlight = true;
    const generation = this.generation;
    try {
      const traffic = await this.streamStats.videoTraffic();
      if (generation !== this.generation) return;
      if (!traffic) {
        this.videoKbps = { down: 0, up: 0 };
        this.videoPrevious = null;
        return;
      }
      const before = this.videoPrevious;
      // A shared snapshot may be reused within its maximum age: no new delta.
      if (before && traffic.at === before.at) return;
      this.videoPrevious = traffic;
      if (!before) return;
      const dt = (traffic.at - before.at) / 1000;
      // Counters restart with a new video connection.
      if (traffic.sent < before.sent || traffic.received < before.received) {
        this.videoKbps = { down: 0, up: 0 };
        return;
      }
      this.videoKbps = {
        down: ((traffic.received - before.received) * 8) / dt / 1000,
        up: ((traffic.sent - before.sent) * 8) / dt / 1000,
      };
    } finally {
      this.videoInFlight = false;
    }
  }

  private reset(): void {
    this.generation += 1;
    this.rttAverage = new MovingAverage(RTT_AVERAGE_WINDOW);
    this.previous = null;
    this.videoKbps = { down: 0, up: 0 };
    this.videoPrevious = null;
    this.rtt.set(0);
    this._latest.set(null);
    this._history.set(emptyHistory());
    this._packetsPerSec.set(null);
    this._connectedSince.set(null);
  }
}

function emptyHistory(): ConnectionHistory {
  return { rtt: [], loss: [], downKbps: [], upKbps: [] };
}

function push(series: number[], value: number): number[] {
  const next = [...series, value];
  return next.length > STATS_HISTORY ? next.slice(-STATS_HISTORY) : next;
}
